import React, { useCallback, useEffect, useState } from "react";
import axios from "../../api";
import {
  Button,
  TextField,
  Grid,
  Box,
  Autocomplete,
  Modal,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import AWS from "aws-sdk";
import { useDropzone } from "react-dropzone";
import { AxiosError } from "axios";
const customScrollbarStyle = {
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#44444461",
    borderRadius: "8px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#666",
  },
};

interface UploadedFile extends File {
  preview: string;
  uniqueId: string;
}

interface Address {
  state_name: string;
  city_name: string;
  region_name: string;
  address_type: string;
  address: string;
  address_id: number;
  division_name: string;
  division_id: number | null;
}

interface Customer {
  customer_id: number;
  name: string;
  email: string;
  phone: string;
  is_priority: boolean;
  is_active: number | boolean;
  type: string;
}

interface FormValues {
  address_id: string;
  customer_id: string;
  asset_name: string;
  asset_sku: string;
  asset_purchased_date: string;
  asset_type_id: string;
  asset_image?: string;
  image_type?: string;
  division_id: string | null;
}

interface AssetType {
  asset_type_id: string;
  asset_type_name: string;
  is_active?: number | boolean;
}

interface AssetProps {
  isOpen: boolean;
  onClose: () => void;
  customers: Customer[] | null;
  fetchData: () => void | Promise<void>;
  /** When true, render form only (no Modal) for use inside SlideOverPanel */
  embedded?: boolean;
  /** When set (e.g. from customer Assets overlay), customer is pre-selected */
  initialCustomerId?: number;
}

interface Division {
  division_id: number;
  division_name: string;
}

const s3 = new AWS.S3({
  endpoint: process.env.REACT_APP_AWS_ENDPOINT,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
  signatureVersion: "v4",
});

const validationSchema = Yup.object({
  address_id: Yup.string().required("Address is required"),
  customer_id: Yup.string().required("Customer is required"),
  asset_name: Yup.string().required("Asset name is required"),
  asset_sku: Yup.string().required("Asset SKU is required"),
  asset_purchased_date: Yup.string()
    .matches(
      /^\d{4}-\d{2}-\d{2}$/,
      "Purchased date must be in the format YYYY-MM-DD"
    )
    .required("Asset purchased date is required")
    .test("is-valid-date", "Purchased date is not a valid date", (value) => {
      return !Number.isNaN(new Date(value).getTime());
    }),
  asset_type_id: Yup.string().required("Asset Type is required"),
});

const AddAsset = ({ isOpen, onClose, customers, fetchData, embedded, initialCustomerId }: AssetProps) => {
  const { fetchError, fetchSuccess } = useSnackbar();

  const makeClientId = React.useCallback((prefix = "id"): string => {
    const g = globalThis as unknown as { crypto?: Crypto };
    if (g.crypto?.randomUUID) return g.crypto.randomUUID();
    if (g.crypto?.getRandomValues) {
      const bytes = new Uint8Array(16);
      g.crypto.getRandomValues(bytes);
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${prefix}-${hex}`;
    }
    // Worst-case fallback (non-crypto environments): still avoid Math.random for Sonar.
    return `${prefix}-${Date.now().toString(36)}-${String(performance?.now?.() ?? 0).replace(".", "")}`;
  }, []);

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(initialCustomerId ?? null);
  const [selectedCustomerType, setSelectedCustomerType] = useState<
    string | null
  >(null);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [assets, setAssets] = useState<AssetType[]>([]);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<Division | null>(
    null
  );
  const today = new Date().toISOString().split("T")[0];

  const dropzone = useDropzone({
    accept: {
      "image/png": [".png", ".jpeg", ".jpg"],
      "video/mp4": [".mp4"],
    },
    onDrop: (acceptedFiles) => {
      setUploadedFiles((prevFiles) =>
        prevFiles.concat(
          acceptedFiles.map((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
              uniqueId: makeClientId(file.name),
            })
          ) as UploadedFile[]
        )
      );
    },
  });

  useEffect(() => {
    const filesWithPreview = dropzone.acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        uniqueId: makeClientId(file.name),
      })
    ) as UploadedFile[];

    setUploadedFiles(filesWithPreview);
  }, [dropzone.acceptedFiles, makeClientId]);

  useEffect(() => {
    if (!selectedCustomer) return;

    const fetchAddressData = async () => {
      try {
        const response = await axios.get(
          `/address/customer/${selectedCustomer}`
        );
        setAddresses(response.data.list);
      } catch (error) {
        console.error("Address fetch error:", error);
      }
    };

    fetchAddressData();
  }, [selectedCustomer]);

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get("/asset-types", {
        params: { is_active: 1 },
      });
      const assetData = Array.isArray(response.data.list)
        ? response.data.list.filter(
            (entry: AssetType) => Number(entry?.is_active ?? 1) === 1
          )
        : [];
      setAssets(assetData);
    } catch (error) {
      if (error instanceof AxiosError) {
        fetchError(error.response?.data?.error ?? "Error loading asset types");
      }
    }
  }, [fetchError]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const fetchDivisions = useCallback(async () => {
    try {
      const response = await axios.get(
        `/divisions/customer/${selectedCustomer}`
      );
      setDivisions(response?.data?.divisions);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);

  const activeCustomers = React.useMemo(
    () =>
      (customers ?? []).filter(
        (entry) =>
          (entry as Customer)?.is_active === 1 ||
          (entry as Customer)?.is_active === true
      ),
    [customers]
  );

  useEffect(() => {
    if (initialCustomerId && activeCustomers.length) {
      const customer = activeCustomers.find(
        (c) => c.customer_id === initialCustomerId
      );
      if (customer) {
        setSelectedCustomer(initialCustomerId);
        setSelectedCustomerType(customer.type ?? "customer");
      }
    }
  }, [initialCustomerId, activeCustomers]);

  const filteredAddresses = React.useMemo(() => {
    if (selectedDivisions?.division_id) {
      return addresses.filter(
        (addr) => addr.division_id === selectedDivisions.division_id
      );
    }
    return addresses;
  }, [addresses, selectedDivisions]);

  const handleAssetPost = async (
    values: FormValues,
    resetForm: any,
    setFieldError?: (field: string, message: string | undefined) => void
  ) => {
    try {
      const response = await axios.post("/assets", values);
      fetchData();
      fetchSuccess(response.data.message);
      resetForm();
      onClose();
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const rawMessage =
          (typeof error.response.data === "string"
            ? error.response.data
            : error.response.data?.error || error.response.data?.message) ||
          "Error creating asset. Please try again.";
        const normalized = rawMessage.toLowerCase();
        if (
          normalized.includes("sku") &&
          (normalized.includes("exists") || normalized.includes("duplicate"))
        ) {
          setFieldError?.("asset_sku", "This SKU already exists");
          fetchError("This SKU already exists");
        } else {
          fetchError(rawMessage);
        }
      }
    }
  };

  // Fixed: Promise rejection with Error object
  const uploadFileToS3 = (file: File, values: FormValues) =>
    new Promise<void>((resolve, reject) => {
      const fileName = `${Date.now()}-${file.name}`;
      values.asset_image = fileName;

      const bucketName = process.env.REACT_APP_AWS_BUCKET_NAME;
      if (!bucketName) {
        const error = new Error("AWS bucket configuration is missing");
        fetchError(error.message);
        reject(error);
        return;
      }

      const params: AWS.S3.PutObjectRequest = {
        Bucket: bucketName,
        Key: fileName,
        Body: file,
        ContentType: file.type,
        ACL: "public-read",
      };

      let type = file.type.split("/")[0];
      values.image_type = type === "image" ? "Photo" : type;

      s3.upload(params, (err, data) => {
        if (err) {
          console.error("Error uploading file:", err);
          reject(new Error(`Failed to upload file: ${err.message}`));
        } else {
          resolve();
        }
      });
    });

  const formContent = (
    <Box
      sx={{
        ...(embedded
          ? { p: 2 }
          : {
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              borderRadius: "8px",
              boxShadow: 24,
              p: 4,
              minWidth: 500,
              minHeight: 220,
              maxHeight: "calc(100vh - 100px)",
              overflow: "auto",
              ...customScrollbarStyle,
            }),
      }}
    >
      {!embedded && (
        <Typography variant="h5" style={{ marginBottom: "18px" }}>
          Add New Asset
        </Typography>
      )}

        <Formik<FormValues>
          key={initialCustomerId ?? "default"}
          initialValues={{
            address_id: "",
            asset_name: "",
            asset_sku: "",
            customer_id: initialCustomerId ? String(initialCustomerId) : "",
            asset_purchased_date: "",
            asset_type_id: "",
            asset_image: "",
            image_type: "",
            division_id: null,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm, setFieldError }) => {
            setSubmitting(true);
            if (!values.division_id) values.division_id = null;

            try {
              if (uploadedFiles?.length > 0) {
                const file = uploadedFiles[0];
                await uploadFileToS3(file, values);
              }

              await handleAssetPost(values, resetForm, setFieldError);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Customer
                    </SoftTypography>
                    <Autocomplete
                      options={activeCustomers}
                      getOptionLabel={(option) =>
                        `${option.name}${option.email ? ` - ${option.email}` : ""}`
                      }
                      value={
                        activeCustomers.find(
                          (c) => c.customer_id === Number(values.customer_id)
                        ) ?? null
                      }
                      onChange={(event, value) => {
                        setSelectedAddress(null);
                        setFieldValue("address_id", "");
                        setFieldValue("customer_id", value?.customer_id ?? "");
                        setSelectedCustomer(value?.customer_id ?? null);
                        setSelectedCustomerType(value?.type ?? "");
                        setFieldValue("division_id", "");
                        setSelectedDivisions(null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.customer_id && touched.customer_id}
                          helperText={touched.customer_id && errors.customer_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                {selectedCustomerType === "company" && (
                  <Grid item xs={12} sm={6}>
                    <SoftBox sx={{ display: "block" }}>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                        Division
                      </SoftTypography>
                      <Autocomplete
                        options={divisions ?? []}
                        getOptionLabel={(option) => option.division_name}
                        value={selectedDivisions ?? null}
                        onChange={(event, value) => {
                          setFieldValue(
                            "division_id",
                            value ? value.division_id : null
                          );
                          setSelectedDivisions(value);
                          setSelectedAddress(null);
                          setFieldValue("address_id", "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.division_id && touched.division_id}
                            helperText={
                              touched.division_id && errors.division_id
                            }
                          />
                        )}
                      />
                    </SoftBox>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Asset Type
                    </SoftTypography>
                    <Autocomplete
                    options={assets ?? []}
                    getOptionLabel={(option) => option.asset_type_name ?? ""}
                    onChange={(event, value) => {
                      setFieldValue(
                        "asset_type_id",
                        value ? value.asset_type_id : ""
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        error={!!errors.asset_type_id && touched.asset_type_id}
                        helperText={
                          touched.asset_type_id && errors.asset_type_id
                        }
                      />
                    )}
                  />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Brand Name
                    </SoftTypography>
                    <Field
                      name="asset_name"
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      error={!!errors.asset_name && touched.asset_name}
                      helperText={touched.asset_name && errors.asset_name}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      SKU
                    </SoftTypography>
                    <Field
                      name="asset_sku"
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      error={!!errors.asset_sku && touched.asset_sku}
                      helperText={touched.asset_sku && errors.asset_sku}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Asset Purchased Date
                    </SoftTypography>
                    <Field
                    className="date-field"
                    name="asset_purchased_date"
                    as={TextField}
                    type="date"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      inputProps: { max: today },
                    }}
                    error={
                      !!errors.asset_purchased_date &&
                      touched.asset_purchased_date
                    }
                    helperText={
                      touched.asset_purchased_date &&
                      errors.asset_purchased_date
                    }
                  />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Address
                    </SoftTypography>
                    <Autocomplete
                      options={filteredAddresses ?? []}
                      value={selectedAddress}
                      getOptionLabel={(option) =>
                        `${option.address},${option.address_type}-${
                          option.region_name
                        }-${option.city_name}-${option.state_name}.${
                          option.division_name ?? ""
                        }`
                      }
                      onChange={(event, value) => {
                        setFieldValue("address_id", value?.address_id ?? "");
                        setSelectedAddress(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.address_id && touched.address_id}
                          helperText={touched.address_id && errors.address_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  {uploadedFiles.length > 0 && (
                    <div className="upload-media">
                      {uploadedFiles.map((file) => (
                        <div
                          key={file.uniqueId}
                          className="media-container"
                          style={{ position: "relative" }}
                        >
                          {file.preview &&
                            (file.type.startsWith("image") ? (
                              <img
                                src={file.preview}
                                alt={`media-${file.name}`}
                                className="upload-img"
                              />
                            ) : (
                              <video
                                className="upload-video"
                                controls
                                width="100%"
                                height="140px"
                              >
                                <source src={file.preview} type="video/mp4" />
                                <track
                                  kind="captions"
                                  srcLang="en"
                                  label="English captions"
                                />
                              </video>
                            ))}
                        </div>
                      ))}
                    </div>
                  )}

                  <Box
                    sx={{ cursor: "pointer", width: "fit-content" }}
                    {...dropzone.getRootProps()}
                  >
                    <Box>
                      <label htmlFor="image-upload-input">
                        <input
                          {...dropzone.getInputProps({
                            name: "asset_image",
                          })}
                        />
                        <Button
                          component="span"
                          variant="contained"
                          color="primary"
                          className="upload-btn"
                        >
                          Upload File
                        </Button>
                      </label>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Button
                    className="success-btn"
                    sx={{
                      position: "relative",
                      minWidth: 100,
                    }}
                    variant="contained"
                    disabled={submitting}
                    type="submit"
                    color="success"
                  >
                    Save
                  </Button>
                  <Button
                    className="cancel-btn"
                    sx={{
                      position: "relative",
                      minWidth: 100,
                      ml: 2.5,
                    }}
                    variant="contained"
                    color="warning"
                    onClick={onClose}
                    type="button"
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
  );

  if (embedded) {
    return isOpen ? formContent : null;
  }
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="otp-modal"
      aria-describedby="otp-modal-description"
    >
      {formContent}
    </Modal>
  );
};

export default AddAsset;
